import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Toast } from '@hb/coffee';
import platform from '@/shared/utils/platform';
import {
  AcceptTaskListParamsType,
  GetRewardingTaskListParams,
  ReceiveAwardParamsType,
  rewardingStatuses,
  RewardingTasksInfo,
  RewardingTaskType,
  TaskType,
} from '@/api/rewarding/types';

import { openThirdPage } from '@/api/native/utils';
import { PerformanceNavigationType } from './constants';

const HomeStore = namespace('home');
const style = require('./index.module.scss') as Dictionary;

@Component
export default class RewardingActivity extends Vue {
  @HomeStore.Action getRewardingTaskList!: (
    params: GetRewardingTaskListParams
  ) => Promise<RewardingTasksInfo>;

  @HomeStore.Action acceptRewardingTaskList!: (
    params: AcceptTaskListParamsType
  ) => Promise<TaskType[]>;

  @HomeStore.Action receiveAward!: (
    params: ReceiveAwardParamsType
  ) => Promise<string>;

  // 是否展示奖励金活动信息
  private showRewardingInfo = false;

  // 是否展示奖励金活动模块
  private showRewardingInfoRewardingContent = false;

  // 展示在首页的奖励金活动信息
  private showedTaskInfos: RewardingTaskType | undefined = undefined;

  // 展示在首页的奖励金活动的活动时间范围
  private dateRange = '';

  // 查看更多按钮跳转链接
  private showMoreTaskLink = '';

  // 是否正在请求数据
  private isRequested = false;

  private async created() {
    await this.refresh();

    // 解决IOS回退时不触发数据刷新问题
    if (platform.isIOS) {
      if (window.performance
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        && window.performance.navigation.type === PerformanceNavigationType.TYPE_BACK_FORWARD) {
        await this.refresh();
      } else {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.addEventListener('pageshow', async (e) => {
          if (e.persisted) {
            await this.refresh();
          }
        });
      }
    }
  }

  private async refresh() {
    let tasksList: RewardingTaskType[] = [];
    const tasksInfo = await this.getRewardingTaskList({
      taskStatusList: [
        rewardingStatuses.init,
        rewardingStatuses.running,
        rewardingStatuses.finished,
      ],
    });
    this.showMoreTaskLink = (tasksInfo && tasksInfo.showMoreTaskLink) || '';
    const taskList = (tasksInfo && tasksInfo.taskList) || [];
    const runnintTaskList = taskList.filter(
      (item: RewardingTaskType) => item.taskStatus === rewardingStatuses.running,
    );
    // 当无进行中状态数据时，再去计算init转台数据
    const initTaskList = !runnintTaskList.length
      ? taskList.filter(
        (item: RewardingTaskType) => item.taskStatus === rewardingStatuses.init,
      )
      : [];
    // 当无running和init状态数据时，再去计算finished状态数据
    const finishedTaskList = !runnintTaskList.length && !initTaskList.length
      ? taskList.filter(
        (item: RewardingTaskType) => item.taskStatus === rewardingStatuses.finished,
      )
      : [];
    tasksList = tasksList.concat(
      runnintTaskList,
      initTaskList,
      finishedTaskList,
    );

    if (tasksList.length > 0) {
      const [task] = tasksList;
      this.showRewardingInfoRewardingContent = true;
      // 只有已完成任务时，仅显示查看更多
      // 反之显示进行中/未开始活动信息
      if (task.taskStatus !== rewardingStatuses.finished) {
        this.showRewardingInfo = true;
        this.showedTaskInfos = task;
      }
    } else {
      // 无 running、init、finished状态的活动时，不显示奖励金模块
      this.showedTaskInfos = undefined;
      this.showRewardingInfoRewardingContent = false;
      this.showRewardingInfo = false;
    }

    if (this.showedTaskInfos) {
      const startDate = this.showedTaskInfos && this.showedTaskInfos.startDate
        ? this.showedTaskInfos.startDate
        : '';

      const endDate = this.showedTaskInfos && this.showedTaskInfos.expireDate
        ? this.showedTaskInfos.expireDate
        : '';
      this.dateRange = `活动时间 ${this.dateFormate(
        startDate,
      )} - ${this.dateFormate(endDate)}`;
    }
    this.isRequested = true;
  }

  // 查看更多奖励金任务
  private onClickWatchMore() {
    this.$ubtPageButton('finance_homeV2_watchMoreRewardingTasks');
    openThirdPage(this.showMoreTaskLink);
  }

  // 时间格式转换："2021-03-28T23:59:59" -> "3月28日"
  private dateFormate(str: string) {
    const date = new Date(str);
    if (date) {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}月${day}日`;
    }
    return '';
  }

  private async onClickButton() {
    if (this.showedTaskInfos) {
      this.$ubtPageButton(
        'finance_homeV2_clickRewardingButton',
        'taskName',
        this.showedTaskInfos.taskName || '',
        'taskStatus',
        this.showedTaskInfos.taskStatus || '',
        '',
        String(this.showedTaskInfos.currentProgress) || '',
      );
      // 用户领取任务
      if (this.showedTaskInfos.taskStatus === rewardingStatuses.init) {
        const params = {
          taskList: [
            {
              taskGroupCode: this.showedTaskInfos.taskGroupCode || '',
              taskCode: this.showedTaskInfos.taskCode || '',
              expertId: this.showedTaskInfos.expertId || '',
              userTags: this.showedTaskInfos.userTags,
            },
          ],
        };
        await this.acceptRewardingTaskList(params);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        Toast('领取成功');
        await this.refresh();
      } else if (
        // 用户领取奖励
        this.showedTaskInfos.taskStatus === rewardingStatuses.finished
      ) {
        const params = {
          taskGroupCode: this.showedTaskInfos.taskGroupCode || '',
          taskGuid: this.showedTaskInfos.taskGuid || '',
        };
        await this.receiveAward(params);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        Toast('奖励金已发放');
        await this.refresh();
      } else if (
        // 用户执行任务
        this.showedTaskInfos.taskStatus === rewardingStatuses.running
      ) {
        const { btnLink } = this.showedTaskInfos;
        if (btnLink) {
          if (platform.isAliMP) {
            this.goToKeyPath(btnLink.ali || '');
          } else {
            this.goToKeyPath(btnLink.app || '');
          }
        }
      }
    }
  }

  private goToKeyPath(url: string) {
    // 如果目标跳转路由属于臻有钱，则使用router.push
    if (url.includes('m.hellobike.com/AppFinanceH5Web/')) {
      const index = url.indexOf('#');
      const path = url.substring(index + 1, url.length);
      this.$router.push(path);
    } else if (url.includes('AppFinanceCreditH5')) {
      window.location.href = url;
    } else {
      openThirdPage(url);
    }
  }

  private render() {
    return (
      this.showRewardingInfoRewardingContent && (
        <div class={style.rewardingContent}>
          <div class={style.top}>
            <span class={style.topLeft}>奖励活动</span>
            <span class={style.topRight} onClick={this.onClickWatchMore.bind(this)}>
              查看更多
            </span>
          </div>

          {this.showRewardingInfo && (
            <div class={style.bottom}>
              <span class={style.bottomIcon} />

              <div class={style.taskInfo}>
                <div class={style.guideDesc}>
                  <span>
                    {this.showedTaskInfos ? this.showedTaskInfos.guideDesc : ''}
                  </span>
                  <span class={style.guideAwardDesc}>
                    {this.showedTaskInfos
                      ? this.showedTaskInfos.guideAwardDesc
                      : ''}
                  </span>
                </div>
                <span class={style.taskTime}>{this.dateRange}</span>
              </div>

              <span class={style.taskButton} onClick={this.onClickButton.bind(this)}>
                {this.showedTaskInfos ? this.showedTaskInfos.btnName : ''}
              </span>
            </div>
          )}
        </div>
      )
    );
  }
}
